/* Small controls shared by the builder steps. */

import { createContext, CSSProperties, ReactNode, useContext, useState } from 'react';
import { Check } from 'lucide-react';
import type { Catalog } from './catalog';
import type { Character } from './model';

export interface StepProps {
  c: Character;
  catalog: Catalog;
  set: (patch: Partial<Character>) => void;
}

interface ChoiceProps {
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}

/** A selectable card. Its accessible name starts with its first visible text. */
export function Choice({ selected, onClick, disabled, className, children }: ChoiceProps) {
  return (
    <button
      type="button"
      className={['cb-choice', selected && 'is-selected', className].filter(Boolean).join(' ')}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** A row of toggle chips; `display` words an option for the chip while `isOn` and `onPick` get the option itself. */
export function Chips({ options, isOn, onPick, label, display = (o) => o }: { options: string[]; isOn: (o: string) => boolean; onPick: (o: string) => void; label?: string; display?: (o: string) => string }) {
  return (
    <div className="cb-chips" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o} type="button" className={`cb-chip${isOn(o) ? ' is-on' : ''}`} aria-pressed={isOn(o)} onClick={() => onPick(o)}>
          {display(o)}
        </button>
      ))}
    </div>
  );
}

interface TextProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  /** Keeps answers short enough to fit their box on the printed page. */
  maxLength?: number;
  className?: string;
}

export function TextField({ label, value, onChange, placeholder, multiline, maxLength, className }: TextProps) {
  return (
    <label className={['cb-field', className].filter(Boolean).join(' ')}>
      <span className="cb-field__label">{label}</span>
      {multiline ? (
        <textarea value={value} placeholder={placeholder} rows={3} maxLength={maxLength} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type="text" value={value} placeholder={placeholder} maxLength={maxLength} onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );
}

export function SelectField({ label, value, options, placeholder, disabled, onChange }: { label: string; value: string; options: string[]; placeholder: string; disabled?: boolean; onChange: (v: string) => void }) {
  return (
    <label className="cb-field">
      <span className="cb-field__label">{label}</span>
      <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

/** True once the player chose Keep building over finishing with choices missing: needed panels then light up. */
export const ShowNeeded = createContext(false);

interface PanelProps {
  title: string;
  children: ReactNode;
  className?: string;
  /** Holds a required choice that is not made yet. */
  needed?: boolean;
  /** A quiet breathing gem by the title, saying what is still to pick. */
  cue?: string;
}

export function Panel({ title, children, className, needed, cue }: PanelProps) {
  const lit = useContext(ShowNeeded) && needed;
  return (
    <section className={['cb-panel', className, lit && 'cb-panel--needed', cue && 'cb-panel--cue'].filter(Boolean).join(' ')} aria-label={title}>
      <h3 className="cb-panel__title">
        {title}
        {cue && <span className="cb-cue" aria-hidden="true" />}
        {lit && <span className="cb-needed-tag">Needed</span>}
      </h3>
      {cue && <p className="cb-cue-text">{cue}</p>}
      {children}
    </section>
  );
}

/** A rules feature: bold name, then its text. */
export function FeatureCard({ name, children }: { name?: string; children: ReactNode }) {
  return (
    <div className="cb-feature">
      {name && <strong className="cb-feature__name">{name}</strong>}
      <div className="cb-feature__text">{children}</div>
    </div>
  );
}

interface PickerProps {
  /** What is being picked, for the "Change" button ("class", "armor"). */
  what: string;
  picked: boolean;
  summary: ReactNode;
  /** The full list; call `close` after a pick to fold it back into the summary. */
  children: (close: () => void) => ReactNode;
}

/** A long list of choices that folds into a summary of the pick, with a button to change it. */
export function Picker({ what, picked, summary, children }: PickerProps) {
  const [browsing, setBrowsing] = useState(!picked);
  if (picked && !browsing) {
    return (
      <div className="cb-picked">
        <div className="cb-picked__body">{summary}</div>
        <button type="button" className="cb-btn cb-picked__change" onClick={() => setBrowsing(true)}>Change {what}</button>
      </div>
    );
  }
  return <>{children(() => setBrowsing(false))}</>;
}

interface BrowseGroup<T> {
  /** Names the group's list ("Ancestries", or a domain). */
  label: string;
  head?: ReactNode;
  style?: CSSProperties;
  items: T[];
}

interface BrowserProps<T> {
  groups: BrowseGroup<T>[];
  name: (item: T) => string;
  /** The item's card from the card library. */
  card: (item: T) => ReactNode;
  taken: (item: T) => boolean;
  /** Takes the shown item, or gives it back when it is already taken. */
  onTake: (item: T) => void;
  /** False while nothing more can be taken (two domain cards chosen). */
  canTake?: boolean;
}

/** Names in a list beside one library card at a time, so a long deck fits one screen. */
export function CardBrowser<T>({ groups, name, card, taken, onTake, canTake = true }: BrowserProps<T>) {
  const all = groups.flatMap((g) => g.items);
  const [shownName, setShownName] = useState<string>();
  const shown = all.find((i) => name(i) === shownName) ?? all.find(taken) ?? all[0];
  if (!shown) return null;
  const isTaken = taken(shown);
  return (
    <div className="cb-browse">
      <div className="cb-browse__list">
        {groups.map((g) => (
          <div key={g.label} role="group" aria-label={g.label} className="cb-browse__group" style={g.style}>
            {g.head}
            {g.items.map((item) => (
              <button
                key={name(item)}
                type="button"
                className={['cb-browse__item', item === shown && 'is-shown', taken(item) && 'is-taken'].filter(Boolean).join(' ')}
                aria-pressed={item === shown}
                onClick={() => setShownName(name(item))}
              >
                {name(item)}
                {taken(item) && <Check size={14} role="img" aria-label="taken" />}
              </button>
            ))}
          </div>
        ))}
      </div>
      <div className="cb-browse__card">
        {card(shown)}
        <button
          type="button"
          className={`cb-btn${isTaken ? '' : ' cb-btn--primary'}`}
          aria-pressed={isTaken}
          disabled={!isTaken && !canTake}
          onClick={() => onTake(shown)}
        >
          {isTaken ? `Taken: ${name(shown)}` : `Take ${name(shown)}`}
        </button>
      </div>
    </div>
  );
}

/** A read-only row of boxes or diamonds, drawn like the sheet's tracks. */
export function Pips({ label, total, open = total, marked, shape }: { label: string; total: number; open?: number; marked: number; shape: 'box' | 'diamond' }) {
  return (
    <div className={`cb-pips cb-pips--${shape}`} role="img" aria-label={`${label}: ${open} slots, ${marked} marked`}>
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className={`cb-pip${i < marked ? ' is-marked' : ''}${i >= open ? ' is-locked' : ''}`} />
      ))}
    </div>
  );
}

/** Replaces item `i` of a list, padding it with blanks. */
export const setAt = (list: string[], i: number, value: string) => {
  const next = [...list];
  while (next.length <= i) next.push('');
  next[i] = value;
  return next;
};

export const toggle = (list: string[], item: string) =>
  list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

/** An example tapped for a list of write-in slots (two Experiences): it fills the first empty slot, or empties the slot it is in. */
export function toggleExample(list: string[], example: string) {
  const at = list.indexOf(example);
  if (at >= 0) return setAt(list, at, '');
  const free = list.findIndex((e) => !e.trim());
  return free >= 0 ? setAt(list, free, example) : list;
}
